/**
 * Date formatting utilities for consistent date display across the application
 */

/**
 * Formats a date string into a user-friendly format
 * @param dateString - The date string to format
 * @returns Formatted date string or "Never" if invalid
 */
export const formatDate = (dateString: string | null | undefined): string => {
  // Handle null, undefined, empty string, or invalid values
  if (!dateString || dateString === 'Unknown' || dateString === 'Invalid Date') {
    return 'Never';
  }

  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Never';
    }

    // Check if date is too far in the past (likely invalid)
    const now = new Date();
    const yearsDiff = now.getFullYear() - date.getFullYear();
    if (yearsDiff > 100 || yearsDiff < -10) {
      return 'Never';
    }

    // Format the date gracefully
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    console.warn('Failed to format date:', dateString, error);
    return 'Never';
  }
};

/**
 * Formats a date string into a relative time format (e.g., "2 days ago")
 * @param dateString - The date string to format
 * @returns Relative time string or "Never" if invalid
 */
export const formatRelativeDate = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return 'Never';
  }

  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Never';
    }

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    } else if (diffInMinutes > 0) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    } else {
      return 'Just now';
    }
  } catch (error) {
    console.warn('Failed to format relative date:', dateString, error);
    return 'Never';
  }
};

/**
 * Formats a date string into a detailed format with time
 * @param dateString - The date string to format
 * @returns Detailed formatted date string or "Never" if invalid
 */
export const formatDetailedDate = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return 'Never';
  }

  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return 'Never';
    }

    // Format the date with time
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.warn('Failed to format detailed date:', dateString, error);
    return 'Never';
  }
};

/**
 * Checks if a date string is valid
 * @param dateString - The date string to validate
 * @returns True if the date is valid, false otherwise
 */
export const isValidDate = (dateString: string | null | undefined): boolean => {
  if (!dateString) {
    return false;
  }

  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
};
